import type { IPromotionPeriod } from '@/interface/IPromotionPeriod';
import { TimezoneOffset } from '@/interface/enum';
import { invertCapitalize } from '@/libraries/string';
import { parseDate } from '@/libraries/utils/date';

const SETTING_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";

export function toInteger(input: string | undefined) {
    const result = Number.parseInt(input ?? '');
    if (Number.isNaN(result)) {
        throw new TypeError(`Failed to parse integer from ${input}`);
    }
    return result;
}

export function toFloat(input: string | undefined) {
    const result = Number.parseFloat(input ?? '');
    if (Number.isNaN(result)) {
        throw new TypeError(`Failed to parse float from ${input}`);
    }
    return result;
}

export function isNotExpired(expiredDate?: string) {
    if (!expiredDate) return false;

    const now = new Date();
    return now < parseDate(expiredDate, SETTING_DATE_FORMAT, TimezoneOffset.Singapore);
}

export function toObject<T>(value?: string): T {
    const result = jsonParse(value);
    if (!result) return {} as T;
    return result as T;
}

export function isEnabled(value?: string, enableValue = 'y') {
    return value?.toLowerCase() === enableValue.toLowerCase();
}

export function toArray<T extends string>(value?: string, delimiter = ',') {
    return value?.split(delimiter).map(x => x as T) ?? [];
}

export function toIntArray<T extends number>(value?: string, delimiter = ',') {
    return value?.split(delimiter).map(x => Number(x) as T) ?? [];
}

export function jsonParse<T>(value?: string) {
    if (!value) return null;

    try {
        return JSON.parse(value, (_, value) => {
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                return Object.keys(value).reduce((newObj: Record<string, unknown>, k) => {
                    newObj[invertCapitalize(k)] = value[k];
                    return newObj;
                }, {});
            }
            return value;
        }) as T;
    } catch (error) {
        console.error('feature toggle JSON parse error:', error);
        return null;
    }
}

export function toTrimmedArray(value?: string, delimiter = ','): string[] {
    return toArray(value, delimiter).map(x => x.trim()).filter(x => x !== '');
}

const ISO_8601_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

function toOffsetDate(value: unknown): Date | null {
    if (typeof value !== 'string' || !ISO_8601_WITH_OFFSET.test(value.trim())) return null;
    const date = new Date(value.trim());
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * `{ "start": "2026-07-22T18:00:00+08:00", "end": "2026-08-08T21:00:00+08:00" }` (format shared with Desktop TCSP-8392).
 * Anything unusable returns null rather than throwing, so a bad value clears the period and the entry stays hidden.
 */
export function toPromotionPeriodOrNull(value?: string): IPromotionPeriod | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(value ?? '');
    } catch {
        return null;
    }
    if (typeof parsed !== 'object' || parsed === null || !('start' in parsed) || !('end' in parsed)) return null;

    const start = toOffsetDate(parsed.start);
    const end = toOffsetDate(parsed.end);
    if (!start || !end || start >= end) return null;
    return { start, end };
}
